import { Injectable } from '@angular/core';
import { AccessoryCharge, PaymentCart } from '@cargos/sprintpay-models';
import { StorageService, StorageTypes } from '@cargos/sprintpay-services';
import dayjs from 'dayjs';

interface AccessoryAdded {
    id: number;
    quantity: number;
    hawbId: string;
    awb: string;
    flightNumber: string;
    flightDate: string;
    paymentType: string;
}

@Injectable({
    providedIn: 'root',
})
export class ExtraItemsService {
    private accessoriesAdded: AccessoryAdded[] = [];

    constructor(private storageService: StorageService) {
        const accessoryAdded: AccessoryAdded[] | null = this.storageService.getElement(
            'accessoriesAdded',
            StorageTypes.SESSION
        );
        if (accessoryAdded) {
            this.accessoriesAdded = accessoryAdded;
        }
    }

    resetAccessoryAdded(): void {
        this.accessoriesAdded = [];
        this.saveAccessoryAdded([]);
    }

    setAndSaveAccessoryAdded(accessoryAdded: AccessoryAdded): void {
        this.setAccessoryAdded(accessoryAdded);
        this.saveAccessoryAdded(this.accessoriesAdded);
    }

    private setAccessoryAdded(accessoryAdded: AccessoryAdded): void {
        this.accessoriesAdded.push(accessoryAdded);
    }

    private saveAccessoryAdded(accessoryAdded: AccessoryAdded[]): void {
        this.storageService.saveElement('accessoriesAdded', accessoryAdded, StorageTypes.SESSION);
    }

    getAccessoryAdded(): AccessoryAdded[] {
        return this.accessoriesAdded;
    }

    getAccessoryAddedByAccessoryCharge(
        accessoryCharge: AccessoryCharge,
        awb: string,
        hawbId: string
    ): AccessoryAdded | undefined {
        return this.accessoriesAdded?.find((accessory) => {
            return (
                accessory.id === accessoryCharge.id &&
                accessory.hawbId === hawbId &&
                accessory.awb === awb &&
                accessory.flightNumber === accessoryCharge.flightNumber &&
                (accessory.flightDate ? dayjs(accessory.flightDate).format('YYYY-MM-DD') : '') ===
                    (accessoryCharge.flightDate ? dayjs(accessoryCharge.flightDate).format('YYYY-MM-DD') : '') &&
                accessory.paymentType === accessoryCharge.paymentType
            );
        });
    }

    getQuantityByAccessorycharge(accessoryCharge: AccessoryCharge, awb: string, hawbId: string): number {
        const accessory = this.getAccessoryAddedByAccessoryCharge(accessoryCharge, awb, hawbId);
        return accessory?.quantity || 0;
    }

    removeAccessory(accessoryCharge: AccessoryCharge, awb: string, hawbId: string): void {
        this.accessoriesAdded = this.accessoriesAdded.filter((accessory) => {
            return !(
                accessory.id === accessoryCharge.id &&
                accessory.hawbId === hawbId &&
                accessory.awb === awb &&
                accessory.flightNumber === accessoryCharge.flightNumber &&
                (accessory.flightDate ? dayjs(accessory.flightDate).format('YYYY-MM-DD') : '') ===
                    (accessoryCharge.flightDate ? dayjs(accessoryCharge.flightDate).format('YYYY-MM-DD') : '') &&
                accessory.paymentType === accessoryCharge.paymentType
            );
        });
        this.saveAccessoryAdded(this.accessoriesAdded);
    }

    removeCharge(charge: PaymentCart): void {
        this.accessoriesAdded = this.accessoriesAdded.filter((accessory) => {
            return !(
                accessory.hawbId === charge.hawb &&
                accessory.awb === charge.awb &&
                accessory.flightNumber === charge.externalData?.flightNumber &&
                (accessory.flightDate ? dayjs(accessory.flightDate).format('YYYY-MM-DD') : '') ===
                    (charge.externalData?.flightDate
                        ? dayjs(charge.externalData?.flightDate).format('YYYY-MM-DD')
                        : '') &&
                accessory.paymentType === charge.externalData?.paymentType
            );
        });
        this.saveAccessoryAdded(this.accessoriesAdded);
    }
}
